// Shared helpers for the Partner Analytics page: colour assignment, response
// normalisation (the API's exact series shape isn't fixed on the frontend
// side, so we defensively support a few common shapes) and label formatting.

export const COLOR_PALETTE = [
	"#3B82F6", // blue
	"#F59E0B", // amber
	"#10B981", // emerald
	"#8B5CF6", // violet
	"#EF4444", // red
	"#06B6D4", // cyan
	"#EC4899", // pink
	"#84CC16", // lime
	"#F97316", // orange
	"#6366F1", // indigo
];

export const METRIC_DEFS = [
	{ key: "revenue", altKeys: ["totalRevenue", "total_revenue"], label: "Revenue", icon: "💰", valueType: "currency" },
	{ key: "payout", altKeys: ["totalPayout", "total_payout"], label: "Payout", icon: "💸", valueType: "currency" },
	{ key: "clicks", altKeys: ["click", "totalClicks", "total_clicks"], label: "Clicks", icon: "🖱️", valueType: "number" },
	{ key: "earned_clicks", altKeys: ["earnedClicks", "total_earned_clicks", "totalEarnedClicks"], label: "Earned Clicks", icon: "🎯", valueType: "number" },
	{
		key: "registered_users",
		altKeys: ["sameDayRegisteredUsers", "same_day_registered_users", "registeredUsers", "same_day_registered", "registeredCount", "registered_count"],
		label: "Same-day Registered Users",
		icon: "👥",
		valueType: "number",
		// Each point's cohort also has a revenue/clicks/earned-clicks breakdown — shown as sub-lines in the tooltip.
		extraMetric: { key: "revenue", label: "Revenue from this cohort", valueType: "currency" },
	},
];

// Limits enforced by the API (kept in sync with the backend validation).
export const MAX_PARTNERS = 20;
export const MAX_MONTHS = 12;
export const MAX_DATE_SPAN_DAYS = 92;

export const PARTNERS_ENDPOINT = "/partnersList";
export const ANALYTICS_ENDPOINT = "/admin/partner-analytics";

export const daysBetween = (sDateStr, eDateStr) => {
	if (!sDateStr || !eDateStr) return 0;
	const s = new Date(`${sDateStr}T00:00:00`);
	const e = new Date(`${eDateStr}T00:00:00`);
	return Math.round((e - s) / (1000 * 60 * 60 * 24));
};

export const getPartnerId = (p) => p?.partner_id ?? p?.partnerId ?? p?._id ?? p?.id ?? null;
export const getPartnerName = (p) => p?.partner_name ?? p?.partnerName ?? p?.name ?? p?.label ?? (getPartnerId(p) ? String(getPartnerId(p)) : "Unknown");

/** Build a stable partnerId -> colour map from the order partners are returned in. */
export const buildColorMap = (partners = []) => {
	const map = {};
	partners.forEach((p, idx) => {
		const id = getPartnerId(p);
		if (id) map[id] = COLOR_PALETTE[idx % COLOR_PALETTE.length];
	});
	return map;
};

/** Converts a "#RRGGBB" (or "#RGB") colour into an "rgba(r,g,b,alpha)" string. */
export const hexToRgba = (hex, alpha = 1) => {
	if (!hex) return `rgba(107,114,128,${alpha})`;
	const clean = hex.replace("#", "");
	const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
	const int = parseInt(full, 16);
	if (isNaN(int)) return `rgba(107,114,128,${alpha})`;
	const r = (int >> 16) & 255;
	const g = (int >> 8) & 255;
	const b = int & 255;
	return `rgba(${r},${g},${b},${alpha})`;
};

const numberFromPoint = (point) => {
	if (point == null) return null;
	if (typeof point === "number") return point;
	if (typeof point === "string") {
		const n = parseFloat(point);
		return isNaN(n) ? null : n;
	}
	if (typeof point === "object") {
		const candidates = ["value", "count", "amount", "total", "y"];
		for (const c of candidates) {
			if (typeof point[c] === "number") return point[c];
			if (typeof point[c] === "string" && point[c] !== "" && !isNaN(parseFloat(point[c]))) return parseFloat(point[c]);
		}
	}
	return null;
};

/**
 * Normalises a single metric's raw payload into:
 *   [{ id, name, data: number[] }, ...]   (data aligned to `labels` by index)
 *
 * Supports a few likely backend shapes:
 *  - Array of per-partner series: [{ partner_id, partner_name, data: [...] }]
 *  - Object map keyed by partnerId: { [partnerId]: [...] }
 *  - Wrapper object: { series: [...] } or { partners: [...] }
 */
export const normalizeMetricSeries = (rawMetricValue, { partners = [], labels = [] } = {}) => {
	if (!rawMetricValue) return [];

	const nameLookup = {};
	partners.forEach((p) => {
		const id = getPartnerId(p);
		if (id) nameLookup[id] = getPartnerName(p);
	});

	const labelIndex = {};
	labels.forEach((l, i) => {
		labelIndex[l] = i;
	});

	const alignData = (rawData) => {
		const arr = new Array(labels.length).fill(null);
		if (!Array.isArray(rawData)) return arr;

		const looksLikeLabeledPoints =
			rawData.length > 0 &&
			typeof rawData[0] === "object" &&
			rawData[0] !== null &&
			(rawData[0].label !== undefined || rawData[0].date !== undefined || rawData[0].month !== undefined || rawData[0].key !== undefined || rawData[0].day !== undefined);

		if (looksLikeLabeledPoints) {
			rawData.forEach((point) => {
				const key = point.label ?? point.date ?? point.month ?? point.key ?? point.day;
				const val = numberFromPoint(point);
				if (key != null && labelIndex[key] !== undefined) {
					arr[labelIndex[key]] = val;
				}
			});
			return arr;
		}

		// Fallback: assume same order/length as `labels`.
		for (let i = 0; i < labels.length; i++) {
			arr[i] = numberFromPoint(rawData[i]);
		}
		return arr;
	};

	// Fields that are structural (id/name/primary-data) rather than an extra
	// per-point breakdown (e.g. `registered_users` series also carry a
	// `revenue` array — the revenue generated by that day's cohort — which we
	// want to surface in the tooltip without treating it as its own metric).
	const STRUCTURAL_KEYS = new Set([
		"partner_id", "partnerId", "_id", "id", "partner", "partner_name", "partnerName", "name", "label",
		"data", "values", "points", "series",
	]);

	const fromSeriesArray = (seriesArr) =>
		seriesArr
			.map((s) => {
				const id = getPartnerId(s) ?? s?.partner ?? null;
				const name = getPartnerName(s) !== "Unknown" ? getPartnerName(s) : nameLookup[id] || id || "Unknown";
				const rawData = s?.data ?? s?.values ?? s?.points ?? s?.series ?? [];

				const extra = {};
				Object.keys(s || {}).forEach((k) => {
					if (STRUCTURAL_KEYS.has(k) || !Array.isArray(s[k])) return;
					extra[k] = alignData(s[k]);
				});

				return { id: id ?? name, name, data: alignData(rawData), extra };
			})
			.filter((s) => s.id);

	if (Array.isArray(rawMetricValue)) {
		return fromSeriesArray(rawMetricValue);
	}

	if (typeof rawMetricValue === "object") {
		if (Array.isArray(rawMetricValue.series)) return fromSeriesArray(rawMetricValue.series);
		if (Array.isArray(rawMetricValue.partners)) return fromSeriesArray(rawMetricValue.partners);

		// Treat as a map of partnerId -> data array
		const entries = Object.entries(rawMetricValue);
		const looksLikePartnerMap = entries.every(([, v]) => Array.isArray(v) || typeof v === "number");
		if (looksLikePartnerMap) {
			return entries.map(([id, v]) => ({
				id,
				name: nameLookup[id] || id,
				data: Array.isArray(v) ? alignData(v) : alignData([v]),
			}));
		}
	}

	return [];
};

const pad2 = (n) => String(n).padStart(2, "0");

export const todayStr = () => {
	const d = new Date();
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export const currentMonthStr = () => {
	const d = new Date();
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

/** Generates ["YYYY-MM", label] options, most recent first. */
export const generateMonthOptions = (pastMonths = 18, futureMonths = 0) => {
	const options = [];
	const now = new Date();
	for (let i = futureMonths; i >= -pastMonths; i--) {
		const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
		options.push({
			value: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`,
			label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
		});
	}
	return options;
};

/** Formats a raw label ("YYYY-MM-DD" or "YYYY-MM") for chart axes. */
export const formatAxisLabel = (rawLabel, mode) => {
	if (!rawLabel) return "";
	if (mode === "month_wise") {
		const [y, m] = String(rawLabel).split("-");
		if (!y || !m) return rawLabel;
		const d = new Date(Number(y), Number(m) - 1, 1);
		return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
	}
	const [y, m, day] = String(rawLabel).split("-");
	if (!y || !m || !day) return rawLabel;
	const d = new Date(Number(y), Number(m) - 1, Number(day));
	return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const formatMetricValue = (value, valueType) => {
	if (value == null || isNaN(value)) return "—";
	if (valueType === "currency") {
		return `${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} coins`;
	}
	return Number(value).toLocaleString();
};
